import { useState } from 'react';
import { redeemFallbackToken } from '../../api/ssoApi.js';
import AppLogo from '../shared/AppLogo.jsx';
import { getDomainBranding } from '../../tenant/tenantConfig.js';
import CredentialFlow from './CredentialFlow.jsx';

/**
 * LoginScreen
 *
 * Rendered on a tenant subdomain when no session is active.
 * Delegates to CredentialFlow for the full credential + MFA flow.
 *
 * Retains sole ownership of:
 *   ?fallback_token=  — emergency admin access (bypasses normal login)
 *   ?mfaRecovery=     — recovery link verifying on the slug domain
 *
 * Phase 2 note: both the slug path (this component) and the universal login
 * path (UniversalLoginPage → CredentialFlow → handoff) run in parallel.
 * Nothing is removed until Phase 3.
 */
function LoginScreen({ login: _login, completeLogin, tenantId, tenant }) {
  const platformName = tenant?.branding?.companyName?.trim() || getDomainBranding().name;
  const logoSrc = tenant?.branding?.logoImage || null;

  // ── Fallback token (emergency access from ?fallback_token= URL param) ─────
  const [fallbackToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('fallback_token') || null;
  });
  const [fallbackLoading, setFallbackLoading] = useState(false);
  const [fallbackError, setFallbackError] = useState('');

  const outer = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg)',
    fontFamily: 'var(--font-body)',
  };
  const inner = { width: '100%', maxWidth: 400, padding: '0 24px' };

  function Header() {
    return (
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <AppLogo size={52} borderRadius={14} style={{ margin: '0 auto 16px' }} src={logoSrc} />
        <div className="display" style={{ fontSize: 26, fontWeight: 600, color: 'var(--fg)' }}>
          {platformName}
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--fg3)',
            marginTop: 4,
            fontFamily: 'var(--font-mono)',
          }}
        >
          Compliance Management Platform
        </div>
      </div>
    );
  }

  const handleFallbackRedeem = async () => {
    if (!fallbackToken) return;
    setFallbackLoading(true);
    setFallbackError('');
    try {
      const result = await redeemFallbackToken({ tenantId, token: fallbackToken });
      if (result.error) {
        setFallbackError(result.error);
        setFallbackLoading(false);
        return;
      }
      localStorage.setItem(`seasuite_token_${tenantId}`, result.token);
      localStorage.setItem(
        `seasuite_session_${tenantId}`,
        JSON.stringify({
          userId: result.user.id,
          username: result.user.username,
          displayName: result.user.displayName,
          role: result.user.role,
          themeId: result.user.themeId || null,
        })
      );
      window.location.reload();
    } catch {
      setFallbackError('Failed to redeem emergency token.');
      setFallbackLoading(false);
    }
  };

  // ── Emergency access (fallback token) ────────────────────────────────────

  if (fallbackToken) {
    return (
      <div style={outer}>
        <div style={inner}>
          <Header />
          <div className="card" style={{ padding: 32 }}>
            <div style={{ marginBottom: 16, fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
              Emergency Access
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg3)', marginBottom: 20, lineHeight: 1.5 }}>
              You have an emergency access token from your administrator. Click below to sign in.
            </div>
            {fallbackError && (
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  background: 'var(--danger-bg)',
                  border: '1px solid rgba(251,113,133,0.25)',
                  color: 'var(--danger)',
                  fontSize: 12,
                  marginBottom: 16,
                }}
              >
                {fallbackError}
              </div>
            )}
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '11px 0', fontSize: 13 }}
              onClick={handleFallbackRedeem}
              disabled={fallbackLoading}
            >
              {fallbackLoading ? 'Signing in…' : 'Sign in with emergency token'}
            </button>
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <a
                href="/"
                style={{
                  fontSize: 12,
                  color: 'var(--accent)',
                  textDecoration: 'none',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                Back to normal sign in
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Read URL params for CredentialFlow ────────────────────────────────────
  const params = new URLSearchParams(window.location.search);
  const urlUsername = params.get('username') || '';
  const mfaRecoveryToken = params.get('mfaRecovery') || null;
  const mfaSetupResetToken = params.get('mfaSetupReset') || null;

  // ── Delegate to CredentialFlow ────────────────────────────────────────────
  return (
    <>
      <CredentialFlow
        tenantId={tenantId}
        tenant={tenant}
        initialUsername={urlUsername}
        autoPasswordless={!!urlUsername}
        recoveryToken={mfaRecoveryToken}
        setupResetToken={mfaSetupResetToken}
        onComplete={(token, user) => completeLogin(token, user)}
      />
      {/* Employee portal link — kept outside CredentialFlow (slug-specific) */}
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          left: 0,
          right: 0,
          textAlign: 'center',
        }}
      >
        <a
          href="/portal-login"
          style={{
            fontSize: 12,
            color: 'var(--accent)',
            textDecoration: 'none',
            fontFamily: 'var(--font-mono)',
          }}
        >
          Employee portal sign in
        </a>
      </div>
    </>
  );
}

export default LoginScreen;
